/**
 * Botón "Recalcular finanzas" reutilizable, visible solo a admin/superadmin.
 *
 * Al click abre un Modal con explicación clara de qué hace y qué NO hace,
 * un checkbox para usar la tasa de comisión actual de la propiedad (default
 * desmarcado), y otro para sobrepasar el bloqueo de ediciones manuales.
 *
 * Llama a `POST /api/stays/recalc-booking/:id` (vía staysRecalcApi). Tras
 * éxito muestra un toast con el resumen del delta (Δ ingreso AF y Δ ingreso
 * propietario) y dispara `onRecalculated()` para que el caller refresque su
 * estado.
 *
 * Se usa en:
 *   - bookingDetailModal.tsx (modal de detalle desde calendario).
 *   - arriendos.tsx (acción por fila en la tabla).
 */
import { useState } from "react";
import { Button, Checkbox, Space, Typography, Tooltip, Modal } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { staysRecalcApi } from "../services/staysRecalc.api";
import { getMsg } from "../utils/antdStatic";
import { isAdminRole } from "../utils/roles.utils";

const { Text, Paragraph } = Typography;

const fmtCLP = (n: number | null | undefined): string => {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(Number(n));
};

const fmtDelta = (antes: number | null, despues: number | null): string => {
  const a = Number(antes ?? 0);
  const d = Number(despues ?? 0);
  const diff = d - a;
  const sign = diff > 0 ? "+" : "";
  return `${fmtCLP(a)} → ${fmtCLP(d)} (${sign}${fmtCLP(diff)})`;
};

interface RecalcBookingButtonProps {
  bookingId: number;
  /** Tasa actual de la propiedad para mostrar en el modal (opcional). */
  tasaComisionActualPct?: number | null;
  /** Tasa histórica del booking para mostrar en el modal (opcional). */
  tasaComisionHistoricaPct?: number | null;
  /** Rol del usuario actual — el botón sólo se renderiza si es admin. */
  userRole: string | string[] | null | undefined;
  /** Callback tras recalc exitoso (para que el caller refresque datos). */
  onRecalculated?: () => void;
  /** Tamaño del botón AntD. */
  size?: "small" | "middle" | "large";
  /** Variante: "button" (botón completo) o "icon-only" (sólo ícono — usar en celdas de tabla). */
  variant?: "button" | "icon-only";
}

/**
 * Tasa numérica almacenada en `booking.tasa_comision_af` está en formato
 * decimal (ej. 0.18 = 18%). En `propiedad.comision_propiedad` está en
 * porcentaje (ej. 18 = 18%). Esta función normaliza a porcentaje para UI.
 */
const toPct = (v: number | null | undefined): number | null => {
  if (v == null) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n > 1 ? n : n * 100;
};

export default function RecalcBookingButton({
  bookingId,
  tasaComisionActualPct,
  tasaComisionHistoricaPct,
  userRole,
  onRecalculated,
  size = "small",
  variant = "button",
}: RecalcBookingButtonProps) {
  const [open, setOpen] = useState(false);
  const [useCurrentTasa, setUseCurrentTasa] = useState(false);
  const [force, setForce] = useState(false);
  const [loading, setLoading] = useState(false);

  // Solo admin/superadmin ven este botón. Cliente y aseador no.
  if (!isAdminRole(userRole)) return null;

  const tasaActual = toPct(tasaComisionActualPct);
  const tasaHist = toPct(tasaComisionHistoricaPct);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const resp = await staysRecalcApi.recalcBooking(bookingId, {
        useCurrentTasaComision: useCurrentTasa,
        force,
      });
      if (!resp) {
        // apiFetch ya mostró el toast de error. Solo cerramos.
        setLoading(false);
        return;
      }
      const msg = getMsg();
      if (!resp.deltas || resp.deltas.length === 0) {
        msg?.info("Nada que actualizar — los valores ya coinciden con la fórmula actual.", 5);
      } else {
        const af = fmtDelta(resp.ingreso_af_clp_antes, resp.ingreso_af_clp_despues);
        const cli = fmtDelta(
          resp.reserva_total_propietario_antes,
          resp.reserva_total_propietario_despues,
        );
        msg?.success(
          `Reserva recalculada. Ingreso AF: ${af}. Ingreso propietario: ${cli}.`,
          7,
        );
      }
      setOpen(false);
      setForce(false);
      // No reseteamos useCurrentTasa para no perder la elección si abre de nuevo.
      onRecalculated?.();
    } finally {
      setLoading(false);
    }
  };

  // Texto compartido del tooltip (i): explica de un vistazo qué hace el
  // botón antes de que el admin lo apriete y abra el modal con el detalle
  // completo. Importante aclarar que NO es replay histórico — solo
  // reprocesa el ÚLTIMO JSON disponible (stays_events guarda por upsert,
  // sin historial).
  const tooltipTitle = (
    <span>
      Reprocesa el último JSON guardado de Stays para esta reserva y vuelve
      a calcular los campos financieros (precio noches, comisiones, IVA,
      ingreso AF, ingreso propietario) y re-sincroniza el aseo y la
      lavandería del arriendo (un aseo ya pagado se preserva). No toca
      fechas, huésped, propiedad ni el bloqueo de salida tardía. Útil cuando
      cambian los costos de la propiedad o el algoritmo financiero.
    </span>
  );

  const triggerLabel = variant === "icon-only" ? null : "Recalcular finanzas";
  const trigger =
    variant === "icon-only" ? (
      <Tooltip title={tooltipTitle}>
        <Button
          size={size}
          icon={<ReloadOutlined />}
          onClick={() => setOpen(true)}
          aria-label="Recalcular finanzas"
        />
      </Tooltip>
    ) : (
      <Tooltip title={tooltipTitle}>
        <Button
          size={size}
          icon={<ReloadOutlined />}
          onClick={() => setOpen(true)}
        >
          {triggerLabel}
        </Button>
      </Tooltip>
    );

  return (
    <>
      {trigger}
      <Modal
        open={open}
        title={
          <Space>
            <ReloadOutlined />
            <span>Recalcular finanzas de esta reserva</span>
          </Space>
        }
        okText={loading ? "Recalculando…" : "Sí, recalcular"}
        cancelText="Cancelar"
        confirmLoading={loading}
        onOk={handleConfirm}
        onCancel={() => {
          if (!loading) {
            setOpen(false);
            setForce(false);
          }
        }}
        maskClosable={!loading}
        width={620}
      >
        <Paragraph>
          Esta acción re-procesa el <Text strong>último JSON</Text> recibido
          de Stays para esta reserva y vuelve a calcular{" "}
          <Text strong>todos</Text> los valores financieros del arriendo:
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: -4 }}>
          ℹ La plataforma manda un único snapshot por reserva (cada
          modificación reemplaza al anterior); no es un replay histórico de
          todos los cambios — es el estado más reciente que reportó la
          plataforma, ya con todas las modificaciones aplicadas.
        </Paragraph>
        <ul style={{ marginTop: -8, marginBottom: 12 }}>
          <li>Precio efectivo de noches.</li>
          <li>Comisión Aloja Fácil + IVA (de noches y de extras).</li>
          <li>Limpieza (suma de los 4 subitems desde la propiedad actual).</li>
          <li>Ingreso AF Empresa.</li>
          <li>Ingreso del propietario.</li>
        </ul>

        <Paragraph>
          <Text strong>Útil cuando:</Text>
        </Paragraph>
        <ul style={{ marginTop: -8, marginBottom: 12 }}>
          <li>
            Cambiaste subitems de limpieza de la propiedad y quieres que esta
            reserva refleje los nuevos valores.
          </li>
          <li>
            El algoritmo de cálculo financiero cambió (cambios en la fórmula,
            en cómo se lee el JSON, etc.) y quieres actualizar reservas
            antiguas sin tocar código de migración.
          </li>
        </ul>

        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Además de las columnas financieras, <Text strong>re-sincroniza</Text>{" "}
          el aseo y la lavandería del arriendo (re-derivándolos del booking; un
          aseo ya <Text strong>pagado</Text> se preserva sin cambios).{" "}
          <Text strong>NO modifica</Text> fechas, huésped, propiedad ni el
          bloqueo de salida tardía.
        </Paragraph>

        <div
          style={{
            background: "#fffbe6",
            border: "1px solid #ffe58f",
            borderRadius: 4,
            padding: "10px 12px",
            marginBottom: 12,
          }}
        >
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>⚠ Atención:</Text> si esta reserva tiene ediciones
            manuales (extensiones de noches, salida tardía con costo, otros
            cargos, override de subitems de limpieza), por defecto el
            recálculo <Text strong>se bloquea</Text> para no pisarlas. Marca
            la opción "Forzar" más abajo si igual quieres recalcular pisando
            esos valores.
          </Paragraph>
        </div>

        <Space direction="vertical" style={{ width: "100%" }}>
          <Checkbox
            checked={useCurrentTasa}
            onChange={(e) => setUseCurrentTasa(e.target.checked)}
            disabled={loading}
          >
            <Text>
              También actualizar la tasa de comisión a la actual de la
              propiedad
              {tasaActual != null ? ` (${tasaActual.toFixed(2)}%)` : ""}.
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Por defecto se conserva la tasa histórica del arriendo
              {tasaHist != null ? ` (${tasaHist.toFixed(2)}%)` : ""} para
              respetar la regla de "snapshot inmutable" de finanzas.
            </Text>
          </Checkbox>

          <Checkbox
            checked={force}
            onChange={(e) => setForce(e.target.checked)}
            disabled={loading}
          >
            <Text>
              Forzar incluso si esta reserva tiene ediciones manuales (pisa
              esos valores).
            </Text>
          </Checkbox>
        </Space>
      </Modal>
    </>
  );
}
